import signalsApi from "../../services/signalsApi";
import { normalizeNonce, isSignable, enrollBody } from "./enrollContract";

/**
 * enrollFlow — THE enrollment sequence. One implementation, reached only
 * through components/strategyautomation/useEnrollment.js, which serves the
 * Strategy Automation tab.
 *
 * ARC-52 removed the second caller: components/SignalsTab.js was deleted when
 * the two branches were consolidated, and services/engineApi.js no longer
 * re-exports an enroll() of its own. There is no second code path to drift.
 * enrollFlow.test.js fails if one grows back.
 *
 *   1. GET  /enroll/nonce?wallet=0x…  → { nonce, message, expiresAtMs }
 *   2. wallet personal_signs `message` VERBATIM (free, no gas, no transaction)
 *   3. POST /enroll { wallet, signature, nonce }
 *
 * There is deliberately NO probe-first bare `POST /enroll { wallet }` step. The
 * deployed engine answers that with a 400 (surfaced as code "HTTP_400"), not a
 * 428/SIGNATURE_REQUIRED, so a probe cannot be distinguished from a real
 * failure and every member's enrollment died on it. The nonce flow is the only
 * enroll entry point the live engine has — we go straight to it.
 *
 * Errors are thrown RAW so each caller can classify once with
 * enrollContract.classifyEnrollError(); this module never writes member-facing
 * copy of its own.
 */

/** Progress states, shared so both callers label the same steps identically. */
export const STEP = Object.freeze({
  IDLE: "idle",
  NONCE: "nonce",
  SIGNING: "signing",
  SUBMITTING: "submitting",
  ENROLLED: "enrolled",
  ERROR: "error",
});

/** True once patches/signalsApi-enroll-nonce.patch is applied. */
export const enrollSiweSupported = () =>
  typeof signalsApi.getEnrollNonce === "function" &&
  typeof signalsApi.enrollWithSignature === "function";

/** Thrown when the SIWE client isn't present — we send NOTHING rather than
 *  fall back to a request we know returns 400. */
export const unsupportedError = () => {
  const e = new Error("SIWE enroll client not available.");
  e.code = "UNSUPPORTED_CLIENT";
  return e;
};

const noop = () => {};

/**
 * Run the full enrollment. Resolves with the engine's enrollment record
 * ({ userId, webhookToken, webhookUrl, messageTemplate }); throws raw on any
 * failure.
 *
 * @param {object}   o
 * @param {string}   o.wallet       connected address
 * @param {function} o.signMessage  (message) => Promise<signature>; the wallet
 *                                  adapter. Receives the server's message and
 *                                  must sign it verbatim.
 * @param {function} [o.onStep]     (STEP) => void, progress reporting
 * @param {function} [o.onChallenge] (challenge) => void, after a signable nonce
 */
export async function runEnrollment({ wallet, signMessage, onStep, onChallenge } = {}) {
  const step = typeof onStep === "function" ? onStep : noop;
  const challengeSeen = typeof onChallenge === "function" ? onChallenge : noop;

  if (!wallet) throw new Error("Connect your wallet before enrolling.");
  if (!enrollSiweSupported()) throw unsupportedError();

  /* 1. GET /enroll/nonce — the server builds the message; we never author it. */
  step(STEP.NONCE);
  const challenge = normalizeNonce(await signalsApi.getEnrollNonce(wallet));

  if (!isSignable(challenge)) {
    // Missing nonce or message — unusable. Refuse rather than prompt the member
    // to sign something we invented.
    throw new Error(
      "The engine returned an incomplete sign-in request. Nothing was signed — please try again."
    );
  }
  challengeSeen(challenge);

  /* 2. personal_sign the server's message VERBATIM. Free, off-chain. */
  step(STEP.SIGNING);
  const signature = await signMessage(challenge.message);

  /* 3. POST /enroll { wallet, signature, nonce } */
  step(STEP.SUBMITTING);
  return signalsApi.enrollWithSignature(
    enrollBody({ wallet, signature, nonce: challenge.nonce })
  );
}

export default runEnrollment;
